// API utility functions for making requests to the backend

const API_URL = import.meta.env.DEV ? 'http://localhost:8787/api' : 'https://organise-after-am-api.icy-breeze-c97d.workers.dev/api';

// Get auth token from localStorage
const getAuthToken = () => {
  const authData = localStorage.getItem('am_auth');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      return parsed.token;
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Base request function
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(`${API_URL}${endpoint}`, config);
    
    if (response.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('am_auth');
      window.location.href = '/auth';
      throw new Error('Unauthorized');
    }

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }
    
    return data;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};

// API methods
export const api = {
  // Auth
  auth: {
    login: (credentials) => apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),
    verify: () => apiRequest('/auth/verify'),
  },

  // Venues
  venues: {
    getAll: () => apiRequest('/venues'),
    getOne: (id) => apiRequest(`/venues/${id}`),
    create: (venue) => apiRequest('/venues', {
      method: 'POST',
      body: JSON.stringify(venue),
    }),
    update: (id, updates) => apiRequest(`/venues/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),
    delete: (id) => apiRequest(`/venues/${id}`, {
      method: 'DELETE',
    }),
  },

  // Events
  events: {
    getAll: () => apiRequest('/events'),
    getOne: (id) => apiRequest(`/events/${id}`),
    create: (event) => apiRequest('/events', {
      method: 'POST',
      body: JSON.stringify(event),
    }),
    update: (id, updates) => apiRequest(`/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),
    delete: (id) => apiRequest(`/events/${id}`, {
      method: 'DELETE',
    }),
  },

  // Contacts
  contacts: {
    getAll: () => apiRequest('/contacts'),
    getOne: (id) => apiRequest(`/contacts/${id}`),
    create: (contact) => apiRequest('/contacts', {
      method: 'POST',
      body: JSON.stringify(contact),
    }),
    update: (id, updates) => apiRequest(`/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),
    delete: (id) => apiRequest(`/contacts/${id}`, {
      method: 'DELETE',
    }),
    import: (contacts) => apiRequest('/contacts/import', {
      method: 'POST',
      body: JSON.stringify({ contacts }),
    }),
  },

  // Subscribers
  subscribers: {
    getAll: () => apiRequest('/subscribers'),
    create: (subscriber) => apiRequest('/subscribers', {
      method: 'POST',
      body: JSON.stringify(subscriber),
    }),
    update: (email, updates) => apiRequest(`/subscribers/${encodeURIComponent(email)}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),
    delete: (email) => apiRequest(`/subscribers/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    }),
  },

  // Transactions
  transactions: {
    getAll: (params) => {
      const queryParams = new URLSearchParams(params).toString();
      return apiRequest(`/transactions${queryParams ? `?${queryParams}` : ''}`);
    },
    create: (transaction) => apiRequest('/transactions', {
      method: 'POST',
      body: JSON.stringify(transaction),
    }),
  },

  // Reports
  reports: {
    financial: (params) => {
      const queryParams = new URLSearchParams(params).toString();
      return apiRequest(`/reports/financial${queryParams ? `?${queryParams}` : ''}`);
    },
  },

  // Settings
  settings: {
    get: () => apiRequest('/settings'),
    update: (settings) => apiRequest('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  },

  // Stats
  stats: {
    get: () => apiRequest('/stats'),
  },

  // Activities
  activities: {
    get: () => apiRequest('/activities'),
  },

  // Artists
  artists: {
    getAll: () => apiRequest('/artists'),
    getOne: (id) => apiRequest(`/artists/${id}`),
    create: (artist) => apiRequest('/artists', {
      method: 'POST',
      body: JSON.stringify(artist),
    }),
    update: (id, updates) => apiRequest(`/artists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),
    delete: (id) => apiRequest(`/artists/${id}`, {
      method: 'DELETE',
    }),
    apply: (application) => apiRequest('/artists/apply', {
      method: 'POST',
      body: JSON.stringify(application),
    }),
  },

  // CRM
  crm: {
    getContacts: () => apiRequest('/crm/contacts'),
    getContact: (id) => apiRequest(`/crm/contacts/${id}`),
    createContact: (contact) => apiRequest('/crm/contacts', {
      method: 'POST',
      body: JSON.stringify(contact),
    }),
    updateContact: (id, updates) => apiRequest(`/crm/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),
    deleteContact: (id) => apiRequest(`/crm/contacts/${id}`, {
      method: 'DELETE',
    }),
    getComments: () => apiRequest('/crm/comments'),
    addComment: (comment) => apiRequest('/crm/comments', {
      method: 'POST',
      body: JSON.stringify(comment),
    }),
  },

  // Maaike AI
  maaike: {
    getTasks: () => apiRequest('/maaike/tasks'),
    getTask: (id) => apiRequest(`/maaike/tasks/${id}`),
    createTask: (task) => apiRequest('/maaike/tasks', {
      method: 'POST',
      body: JSON.stringify(task),
    }),
    approveTask: (id, feedback) => apiRequest(`/maaike/tasks/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(feedback),
    }),
    rejectTask: (id, feedback) => apiRequest(`/maaike/tasks/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(feedback),
    }),
    chat: (message) => apiRequest('/maaike/chat', {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),
  },
};
// Cache bust: Thu Jun 12 22:26:03 BST 2025
